<template>
  <AiEducationLayout>
    <EducationPageHeader title="校长工作台" description="查看当前账号获授权的学校治理范围" />
    <ContentWrap>
      <EducationState v-if="status === 'loading'" state="loading" title="正在加载校长工作台" />
      <EducationState
        v-else-if="status === 'error'"
        state="error"
        title="校长工作台加载失败"
        description="请确认当前账号已配置有效的学校管理关系。"
        @retry="load"
      />
      <EducationState
        v-else-if="!schoolIds.length"
        state="empty"
        title="暂无授权学校"
        description="请联系平台管理员配置校长学校范围。"
      />
      <template v-else>
        <el-alert
          title="学校范围和汇总均由后端按当前校长授权学校过滤；与平台概览全量数据口径不同属正常。"
          type="info"
          :closable="false"
          class="mb-12px"
        />
        <el-alert
          v-if="summaryEmpty"
          title="授权学校下暂无学情明细（完成章节/测验等为 0）。概览页若有数据，多为平台全量或其他学校范围，请以本页授权学校为准。"
          type="warning"
          :closable="false"
          class="mb-12px"
        />
        <el-row v-if="summary" :gutter="16" class="summary-grid">
          <el-col v-for="item in summaryCards" :key="item.label" :xs="12" :sm="8" :lg="4">
            <div class="summary-card"
              ><span>{{ item.label }}</span
              ><strong>{{ item.value }}</strong></div
            >
          </el-col>
        </el-row>
        <el-row :gutter="16" class="school-grid">
          <el-col v-for="school in authorizedSchools" :key="school.id" :xs="24" :sm="12" :lg="8">
            <el-card shadow="never" class="school-card">
              <span>授权学校</span>
              <strong>{{ school.name }}</strong>
              <small>{{ school.eduStageLabel || '可访问范围由后端校验' }}</small>
            </el-card>
          </el-col>
        </el-row>
      </template>
    </ContentWrap>
  </AiEducationLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import AiEducationLayout from '../layout/AiEducationLayout.vue'
import EducationPageHeader from '../components/EducationPageHeader.vue'
import EducationState from '../components/EducationState.vue'
import { useEducationWorkspace } from '../composables/useEducationWorkspace'
import {
  getPrincipalAnalyticsSummary,
  getSchoolOptions,
  type StudentSummaryVO,
  type SchoolVO
} from '@/api/aiEducation'
import { DICT_TYPE, getDictLabel } from '@/utils/dict'

defineOptions({ name: 'AiEducationPrincipalHome' })
const { workspace, initialize } = useEducationWorkspace()
const status = ref<'loading' | 'ready' | 'error'>('loading')
const summary = ref<StudentSummaryVO>()
const schoolMap = ref<Record<number, SchoolVO>>({})
const schoolIds = computed(() => workspace.value?.dataScope.schoolIds || [])
const authorizedSchools = computed(() => {
  const orgs = workspace.value?.organizations || []
  return schoolIds.value.map((id) => {
    const fromOrg = orgs.find((o) => o.organizationId === id)
    const fromApi = schoolMap.value[id]
    const name =
      fromApi?.name || fromOrg?.organizationName || (id != null ? `学校（编号 ${id}）` : '未命名学校')
    const eduStage = fromApi?.eduStage || (fromOrg as any)?.eduStage
    return {
      id,
      name,
      eduStageLabel: eduStage
        ? getDictLabel(DICT_TYPE.AI_EDU_STAGE, eduStage) || eduStage
        : ''
    }
  })
})
const load = async () => {
  status.value = 'loading'
  try {
    await initialize()
    const [sum, schools] = await Promise.all([
      getPrincipalAnalyticsSummary().catch(() => undefined),
      getSchoolOptions().catch(() => [] as SchoolVO[])
    ])
    summary.value = sum
    const map: Record<number, SchoolVO> = Object.fromEntries(
      (schools || []).filter((s) => s.id != null).map((s) => [s.id!, s])
    )
    // 下拉接口不全时，用工作台组织名补齐，避免显示「#1」
    for (const org of workspace.value?.organizations || []) {
      const id = org.organizationId
      if (id != null && !map[id]?.name && org.organizationName) {
        map[id] = {
          ...(map[id] || { id }),
          id,
          name: org.organizationName,
          eduStage: (org as any).eduStage
        }
      }
    }
    schoolMap.value = map
    status.value = 'ready'
  } catch {
    status.value = 'error'
  }
}
const summaryCards = computed(() =>
  summary.value
    ? [
        { label: '学习时长', value: `${Math.round(summary.value.studySeconds / 60)} 分钟` },
        { label: '完成章节', value: summary.value.completedChapters },
        { label: '提交作业', value: summary.value.assignmentsSubmitted },
        { label: '答题正确率', value: `${Math.round(summary.value.correctRate * 100)}%` },
        { label: 'AI 请求', value: summary.value.aiRequests },
        { label: '学生范围', value: '已授权' }
      ]
    : []
)
const summaryEmpty = computed(() => {
  const s = summary.value
  if (!s) return false
  return (
    !s.completedChapters &&
    !s.assignmentsSubmitted &&
    !s.aiRequests &&
    !s.studySeconds &&
    !(s.quizAttempts || 0)
  )
})
onMounted(load)
</script>

<style scoped>
.school-grid {
  margin-top: 18px;
}
.summary-grid {
  margin: 18px 0;
}
.summary-card {
  min-height: 86px;
  margin-bottom: 16px;
  padding: 16px;
  border: 1px solid #e5e9ec;
  border-radius: 8px;
  background: #fff;
}
.summary-card span {
  display: block;
  color: #697681;
  font-size: 13px;
}
.summary-card strong {
  display: block;
  margin-top: 10px;
  color: #19705d;
  font-size: 20px;
}
.school-card {
  margin-bottom: 16px;
}
.school-card :deep(.el-card__body) {
  display: flex;
  min-height: 112px;
  flex-direction: column;
  gap: 8px;
}
.school-card span,
.school-card small {
  color: #697681;
}
.school-card strong {
  color: #19705d;
  font-size: 24px;
}
.mb-12px {
  margin-bottom: 12px;
}
</style>
